import React, { useState, useRef, useEffect } from 'react';
import { BookOpen, Clock, MapPin, Edit, Trash2, MoreVertical, XCircle } from 'lucide-react';
import type { CaseData } from '@/types/api';
import { getParentCaseStatusAppearance } from '@/constants/parentCaseStatus';
import { formatCaseSummary, formatCaseTitle, getCaseServiceType } from '@/utils/musicDisplay';
import { useOptionalI18n } from '@/i18n';
import { localizeStringList, localizeText } from '@/i18n/options';

interface CaseCardProps {
  caseData: CaseData;
  onEdit?: (caseId: string) => void;
  onDelete?: (caseId: string) => void;
  onViewDetails?: (caseId: string) => void;
  onResubmit?: (caseId: string) => void;
}

export const CaseCard: React.FC<CaseCardProps> = ({
  caseData,
  onEdit,
  onDelete,
  onViewDetails,
  onResubmit,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { locale } = useOptionalI18n();
  const isZh = locale === 'zh-CN';
  const menuRef = useRef<HTMLDivElement>(null);
  const st = getParentCaseStatusAppearance(caseData.status);
  const isClickable = Boolean(onViewDetails);
  const title = formatCaseTitle(caseData);
  const summary = formatCaseSummary(caseData);
  const serviceType = getCaseServiceType(caseData);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      className={`bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transition-shadow ${isClickable ? 'cursor-pointer hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2' : 'hover:shadow-md'}`}
      onClick={isClickable ? () => onViewDetails?.(caseData.id) : undefined}
      onKeyDown={isClickable ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onViewDetails?.(caseData.id);
        }
      } : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable ? (isZh ? `查看 ${title} 的详情` : `View details for ${title}`) : undefined}
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <span className="inline-block text-xs font-semibold text-primary bg-primary/10 px-2.5 py-0.5 rounded-full mb-2">
              {serviceType === 'music' ? 'Music' : summary}
            </span>
            <h3 className="text-xl font-bold text-gray-900 truncate">{serviceType === 'music' ? title : caseData.subject}</h3>
            <p className="text-xs text-gray-400 mt-1">
              {new Date(caseData.createdAt).toLocaleDateString(isZh ? 'zh-CN' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${st.pill}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
              {st.label}
            </span>
            <div className="relative" ref={menuRef}>
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  setIsMenuOpen(!isMenuOpen);
                }}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {isMenuOpen && (
                <div className="absolute right-0 mt-1 w-40 bg-white rounded-xl shadow-lg border border-gray-100 z-dropdown py-1">
                  {caseData.status === 'pending_review' && (
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        onEdit?.(caseData.id);
                        setIsMenuOpen(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Edit className="h-3.5 w-3.5" /> {isZh ? '编辑' : 'Edit'}
                    </button>
                  )}
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      onDelete?.(caseData.id);
                      setIsMenuOpen(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> {isZh ? '删除' : 'Delete'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {caseData.status === 'pending_review' && (
        <div className="mx-5 mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
          {isZh ? '等待管理员审核中，暂时不会显示在平台列表中。' : 'Awaiting admin approval — not visible on the Marketplace yet.'}
        </div>
      )}

      {/* Info */}
      <div className="px-5 py-4 space-y-2.5">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <MapPin className="h-4 w-4 text-gray-400 shrink-0" />
          <span>{caseData.areaNote ? `${caseData.areaNote}, ` : ''}{localizeText(locale, caseData.state)}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Clock className="h-4 w-4 text-gray-400 shrink-0" />
          <span>{isZh ? `每周 ${caseData.lessonsPerWeek} 次 · 每次 ${caseData.lessonDuration} 小时` : `${caseData.lessonsPerWeek}× per week · ${caseData.lessonDuration}h each`}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <BookOpen className="h-4 w-4 text-gray-400 shrink-0" />
          <span>{localizeStringList(locale, caseData.teachingLanguages).join(' · ')}</span>
        </div>
      </div>

      {/* Rejection reason */}
      {caseData.status === 'rejected' && caseData.rejectionReason && (
        <div className="mx-5 mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex gap-2">
          <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
          <div className="text-sm text-red-700">
            <p className="font-semibold text-xs text-red-800 mb-0.5">{isZh ? '拒绝原因' : 'Rejection Reason'}</p>
            {caseData.rejectionReason}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
        <div>
          <span className="text-lg font-bold text-primary">RM {caseData.hourlyRate ?? (isZh ? '待定' : 'TBD')}</span>
          <span className="text-xs text-gray-400 ml-1">/hr</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs px-2.5 py-1 rounded-lg bg-white border border-gray-200 text-gray-600 font-medium">
            {caseData.tuitionType === 'online' ? (isZh ? '💻 线上' : '💻 Online') : (isZh ? '📍 线下' : '📍 Physical')}
          </span>
          {caseData.status === 'rejected' && onResubmit && (
            <button
              onClick={(event) => {
                event.stopPropagation();
                onResubmit(caseData.id);
              }}
              className="text-xs px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
            >
              {isZh ? '重新提交' : 'Resubmit'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
